import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Divider,
} from "@mui/material";
import StatusChip from "../../utils/StatusChip";
import { Close as CloseIcon } from "@mui/icons-material";

interface Agent {
  id: string;
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  email?: string | null;
  cellnumber?: string | null;
  countrycode?: string | null;
  agentcode?: string | null;
  roleId?: number;
  groupId?: string;
  userstatus?: string | number | null;
  created?: string;
  modified?: string;
  group?: {
    id?: string;
    name?: string;
  };
  billingaddress?: any;
  shippingaddress?: any;
  profilepic?: string | null;
}

interface AgentViewModalProps {
  open: boolean;
  onClose: () => void;
  agent: Agent | null;
}

const AgentViewModal: React.FC<AgentViewModalProps> = ({
  open,
  onClose,
  agent,
}) => {
  if (!agent) return null;

  // Helper functions
  const getAgentName = (agent: Agent) => {
    if (agent.firstname && agent.lastname)
      return `${agent.firstname} ${agent.lastname}`;
    return agent.firstname || agent.lastname || agent.username || "Unknown";
  };

  const parseAddresses = (field: any) => {
    if (!field) return [];
    let arr: any[] = [];

    // Accept stringified JSON, array or single object
    if (typeof field === "string") {
      try {
        const parsed = JSON.parse(field);
        arr = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        return [];
      }
    } else if (Array.isArray(field)) {
      arr = field;
    } else if (typeof field === "object") {
      arr = [field];
    } else {
      return [];
    }

    // Normalize different address shapes to a common shape used by the UI
    const normalize = (a: any) => {
      // New format example keys:
      // address_id, firstname, lastname, company, address_1, address_2, city, postcode, country_name, zone_name
      // Old format example keys (previous UI): id, name, addressline1, addressline2, city, state, zipcode, country, countrycode, phone

      const name =
        (a.firstname || a.firstname === ""
          ? `${(a.firstname || "").trim()} ${(a.lastname || "").trim()}`.trim()
          : null) ||
        a.company ||
        a.name ||
        `${a.firstname || ""} ${a.lastname || ""}`.trim() ||
        a.receiverName ||
        "";

      const addressline1 =
        a.address_1 || a.addressline1 || a.addressLine1 || a.addressline || "";
      const addressline2 =
        a.address_2 || a.addressline2 || a.addressLine2 || a.address2 || "";
      const city = a.city || a.zone_name || a.city_name || a.town || "";
      const state = a.state || a.zone_name || a.region || "";
      const zipcode = a.postcode || a.zipcode || a.zip || a.postal_code || "";
      const country = a.country_name || a.country || a.countryName || "";
      // phone/countrycode may not be present in some payloads
      const countrycode = a.countrycode || a.country_code || a.country_id || "";
      const phone = a.phone || a.telephone || a.mobile || a.contact || "";

      return {
        id: a.address_id || a.id || a.addressId || a.address_id || undefined,
        name: name || "-",
        addressline1: addressline1 || "-",
        addressline2: addressline2 || "",
        city: city || "-",
        state: state || "",
        zipcode: zipcode || "-",
        country: country || "-",
        countrycode: countrycode || "",
        phone: phone || "",
        raw: a,
      };
    };

    return arr.map(normalize);
  };

  const getAgentPhone = (agent: Agent) => {
    if (!agent.cellnumber) return "-";
    const cc = agent.countrycode
      ? agent.countrycode.toString().startsWith("+")
        ? agent.countrycode
        : `+${agent.countrycode}`
      : "";
    return `${cc ? cc + " " : ""}${agent.cellnumber}`;
  };

  const getAgentStatus = (agent: Agent) => {
    if (
      agent.userstatus === 1 ||
      agent.userstatus === "ACTIVE" ||
      agent.userstatus === "Active"
    )
      return 1;
    if (
      agent.userstatus === 0 ||
      agent.userstatus === "DEACTIVE" ||
      agent.userstatus === "Deactive" ||
      agent.userstatus === "Inactive"
    )
      return 0;
    return 0;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          // wider modal to accomodate 3-column layout and avoid internal scrolling
          width: "1000px",
          maxWidth: "98vw",
          // allow content to grow; avoid internal scrolling so the modal uses page scroll if needed
          maxHeight: "none",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" fontWeight={800}>
            Agent Details
          </Typography>
          <Button onClick={onClose} sx={{ minWidth: "auto", p: 1 }}>
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, overflow: "visible" }}>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1.25, pb: 1 }}
        >
          {/* Header Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, pb: 1 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: "primary.main",
                fontSize: "1rem",
                fontWeight: "bold",
                border: "1px solid",
                borderColor: "primary.light",
              }}
            >
              {getAgentName(agent).charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {getAgentName(agent)}
              </Typography>
              <StatusChip status={getAgentStatus(agent)} />
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Contact Information */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "primary.main", mb: 1 }}
            >
              Contact Information
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Phone Number
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {getAgentPhone(agent)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Email
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {agent.email || "-"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Agent Code
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {agent.agentcode || "-"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Username
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {agent.username || "-"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Timestamps */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "primary.main", mb: 1 }}
            >
              Timestamps
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Created
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {formatDate(agent.created || "")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Modified
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {formatDate(agent.modified || "")}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Group & Role */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "primary.main", mb: 1 }}
            >
              Group & Role
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Group
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {agent.group?.name || agent.groupId || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Role
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.9rem" }}
                >
                  {"Agent"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Addresses */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "primary.main", mb: 1 }}
            >
              Addresses
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Billing
                </Typography>
                {parseAddresses(agent.billingaddress).length === 0 ? (
                  <Typography variant="body2">-</Typography>
                ) : (
                  parseAddresses(agent.billingaddress).map((a: any) => (
                    <Box
                      key={a.id || a.phone}
                      sx={{
                        mt: 0.5,
                        p: 0.75,
                        border: "1px dashed",
                        borderColor: "grey.200",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ fontSize: "0.95rem" }}
                      >
                        {a.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {a.addressline1}
                        {a.addressline2 ? `, ${a.addressline2}` : ""}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {a.city}
                        {a.state ? `, ${a.state}` : ""} {a.zipcode}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {a.country}
                        {a.countrycode || a.phone
                          ? ` • ${a.countrycode ? `+${a.countrycode} ` : ""}${
                              a.phone || ""
                            }`
                          : ""}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Shipping
                </Typography>
                {parseAddresses(agent.shippingaddress).length === 0 ? (
                  <Typography variant="body2">-</Typography>
                ) : (
                  parseAddresses(agent.shippingaddress).map((a: any) => (
                    <Box
                      key={a.id || a.phone}
                      sx={{
                        mt: 0.5,
                        p: 0.75,
                        border: "1px dashed",
                        borderColor: "grey.200",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ fontSize: "0.95rem" }}
                      >
                        {a.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {a.addressline1}
                        {a.addressline2 ? `, ${a.addressline2}` : ""}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {a.city}
                        {a.state ? `, ${a.state}` : ""} {a.zipcode}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {a.country}
                        {a.countrycode || a.phone
                          ? ` • ${a.countrycode ? `+${a.countrycode} ` : ""}${
                              a.phone || ""
                            }`
                          : ""}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          sx={{ minWidth: 80 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentViewModal;
